import { Recurrence } from '@tableau/self-service-schedules';
import { IPromise } from 'angular';
import { call, put, select } from 'redux-saga/effects';
import _ from 'underscore';

import { Messages } from '../../../../codegen/Localize';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { RefreshPropertyHelper } from '../../../../ts/helpers/RefreshPropertyHelper';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { IDataSourceBase, IDataSourceDetailInfo, IDataSourceInfo } from '../../../../ts/models/IDataSourceInfo';
import { IWorkbookBase, IWorkbookInfo } from '../../../../ts/models/IWorkbookInfo';
import { Set } from '../../../../ts/models/Set';
import { CreateExtractTasksSelfServiceSchedulesRequest, RefreshTargetType, RefreshTaskType } from '../../../../ts/ServerApi/requests/CreateSelfServiceScheduleExtractTasksRequest';
import { SetDatasourceRefreshModeRequest } from '../../../../ts/ServerApi/requests/SetDatasourceRefreshModeRequest';
import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import { SelfServiceSchedule } from '../../../../ts/ServerApi/types/SelfServiceSchedule';
import { WebHttpClientWithErrorToast, WebHttpClientWithErrorToastInstance } from '../../../../httpClient/WebHttpClientWithErrorToast';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { CreateExtractRefreshAction } from '../../../actions/spaces/CreateExtractRefreshAction';
import { incrementRouterSpaceReactKey } from '../../../actions/spaces/IncrementRouterSpaceReactKeyAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { getAppStore } from '../../../AppStore';
import { OpenRefreshSelfServiceScheduleDialogParameters, RefreshSelfServiceScheduleDialog } from '../../../content/RefreshSelfServiceScheduleDialog';
import { selectContentTotalLength } from '../../../selectors/ContentSelectors';
import { SessionSiteSelectors } from '../../../selectors/SessionInfoSelectors';
import { selectSpaceInfo } from '../../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../../ServiceLocator';
import { SpaceInfo } from '../../../state/SpaceInfo';
import { getDatasourceInfos, getWorkbookInfos } from '../SpacesSagaHelpers';
import {SetDatasourceUsingRemoteQueryAgentRequest} from '../../../../ts/ServerApi/requests/SetDatasourceUsingRemoteQueryAgentRequest';
import { CreateExtractTaskActionService, CreateExtractTasksMode } from '~src/extract/actions/CreateExtractTaskAction';
import { CreateWorkbookExtractTaskActionService } from '~src/extract/actions/CreateWorkbookExtractTaskAction';
import { CreateDatasourceExtractTaskActionService } from '~src/extract/actions/CreateDatasourceExtractTaskAction';
import { CreateMixedContentExtractTaskActionService } from '~src/extract/actions/CreateMixedContentExtractTaskAction';


export function openWorkbookRefreshExtractDialog(
  workbooks: Set<IWorkbookBase>,
  mode: CreateExtractTasksMode
): IPromise<IResult> {
  const context = ServiceLocator.getAngularActionContext();
  return CreateWorkbookExtractTaskActionService.forWorkbooks(workbooks, context, mode);
}

export function openDatasourceRefreshExtractDialog(
  datasources: Set<IDataSourceBase>,
  mode: CreateExtractTasksMode
): IPromise<IResult> {
  const context = ServiceLocator.getAngularActionContext();
  return CreateDatasourceExtractTaskActionService.forDatasources(datasources, context, mode);
}

export function openContentRefreshExtractDialog(
  content: Set<IContentInfo>,
  mode: CreateExtractTasksMode
): IPromise<IResult> {
  const context = ServiceLocator.getAngularActionContext();
  return CreateMixedContentExtractTaskActionService.forContent(content, context, mode);
}

export function setExtractRefreshMode(
  refreshMode: string,
  action: CreateExtractRefreshAction
){
  const datasources = action.selectedContent.filter(c => c.contentType === 'datasource');
  const dsIds = datasources.map(content => (content.id));

  for(const dsId of dsIds) {
    const changeModeRequest = new SetDatasourceRefreshModeRequest({
      id: dsId,
      refreshMode: refreshMode,
    });
    WebHttpClientWithErrorToastInstance.sendRequest(changeModeRequest).then(result => {
      getAppStore().dispatch(incrementRouterSpaceReactKey());
    }).catch(err => {
      CreateExtractRefreshAction.notifyResult();
    });
  }
}


export function* createSelfServiceScheduleExtractAction(action: CreateExtractRefreshAction) {
  const contentDictionaryByType = _.groupBy(action.selectedContent, c => c.contentType);
  const workbookInfos = contentDictionaryByType['workbook'] && (yield call(getWorkbookInfos, contentDictionaryByType['workbook']));
  const datasourceInfos = contentDictionaryByType['datasource'] && (yield call(getDatasourceInfos, contentDictionaryByType['datasource']));
  let targetResources: (IDataSourceInfo | IWorkbookInfo)[] = [];
  if (workbookInfos) {
    targetResources = targetResources.concat(workbookInfos);
  }
  if (datasourceInfos) {
    targetResources = targetResources.concat(datasourceInfos);
  }
  let params = yield call(CreateExtractTaskActionService.getRefreshSSSDialogParams, targetResources);
  let result = yield call(RefreshSelfServiceScheduleDialog.open, params);
  const $q = ServiceLocator.get<ng.IQService>('$q');
  return $q.resolve(result);
}

export function* handleCreateExtractRefreshAction(action: CreateExtractRefreshAction) {
  const isExtractSSSEnabledForServer = FeatureFlags.selfServiceSchedulesForRefreshEnabled;
  const isExtractSSSEnabledForSite = SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(getAppStore().getState());
  const isCreateBridgeExtractRefresh = (action.mode === CreateExtractTasksMode.AllowScheduleOnlyOnClient);
  if (isExtractSSSEnabledForServer && isExtractSSSEnabledForSite && !isCreateBridgeExtractRefresh) {
    yield call(createSelfServiceScheduleExtractAction, action);
    yield call(setExtractRefreshMode, 'Server', action);
    if(isRefreshUsingRemoteAgent(action)){
      yield call(setUsingRemoteAgent, action);
    }
    yield put(requestSpaceContents());
  } else {
    const localizedErrorMessage = Messages.unexpectedError();
    try {
      const contentDictionaryByType = _.groupBy(action.selectedContent, c => c.contentType);
      const typeArray = Object.keys(contentDictionaryByType);

      if (typeArray.length === 1) {
        const singleContentType = typeArray[0];
        switch (singleContentType) {
          case 'workbook':
            const workbookInfos = yield call(getWorkbookInfos, contentDictionaryByType[singleContentType]);
            const workbookSet = Set.fromIdItemArray(workbookInfos);
            yield call(openWorkbookRefreshExtractDialog, workbookSet, action.mode);
            break;
          case 'datasource':
            const datasourceInfos = yield call(getDatasourceInfos, contentDictionaryByType[singleContentType]);
            const datasourceSet = Set.fromIdItemArray(datasourceInfos);
            yield call(openDatasourceRefreshExtractDialog, datasourceSet, action.mode);
            break;
          default:
            yield put(requestFailed(`${singleContentType} does not support extract refresh.`));
            break;
        }
      } else if (typeArray.length > 1) {
        const workbookInfos = yield call(getWorkbookInfos, contentDictionaryByType['workbook']);
        const datasourceInfos = yield call(getDatasourceInfos, contentDictionaryByType['datasource']);
        const contentIds = Set.fromIdItemArray(workbookInfos.concat(datasourceInfos));
        yield call(openContentRefreshExtractDialog, contentIds, action.mode);
      }

      const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
      if (spaceInfo.subspaceName === 'extractTasks' || spaceInfo.subspaceName === 'extractRefreshes') {
        yield put(requestSpaceContents());
      }
    } catch (e) {
      yield put(requestFailed(e, localizedErrorMessage));
    }
  }
}

function isRefreshUsingRemoteAgent(action: CreateExtractRefreshAction) {
  return action.mode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient ||
    action.mode === CreateExtractTasksMode.AllowScheduleOnServerAndClient;
}

function setUsingRemoteAgent(action: CreateExtractRefreshAction) {
  const datasources = action.selectedContent.filter(c => c.contentType === 'datasource');
  const dsIds = datasources.map(content => (content.id));

  for (const dsId of dsIds) {
    const setDatasourceUsingRemoteQueryAgentRequest = new SetDatasourceUsingRemoteQueryAgentRequest({
      id: dsId,
    });
    WebHttpClientWithErrorToastInstance.sendRequest(setDatasourceUsingRemoteQueryAgentRequest)
    .catch(err => {
      CreateExtractRefreshAction.notifyResult();
    });
  }
}